import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  Users,
  Building,
  Mail,
  Shield,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABLE_SERVICES = [
  { id: 'mitarbeiterabgleich', name: 'Mitarbeiterabgleich' },
  { id: 'gesamtabsolvierungen', name: 'Gesamtabsolvierungen' },
  { id: 'bereinigung_teams', name: 'Bereinigung Teams' },
  { id: 'csv_transformer', name: 'CSV Transformer' },
];

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // e-Matrix color constants
  const colors = {
    dark: '#0D2440',
    blue: '#2E5E99',
    gray: '#5A7A9B',
    border: '#C8D8E8',
    bg: '#E7F0FA',
  };
  
  // Modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    customer_code: '',
    is_admin: false,
    services: {},
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      company: '',
      customer_code: '',
      is_admin: false,
      services: AVAILABLE_SERVICES.reduce((acc, s) => ({ ...acc, [s.id]: 'disabled' }), {}),
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/admin/users`, formData);
      toast.success('Benutzer erfolgreich erstellt');
      setIsCreateOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      delete updateData.email;
      
      await axios.put(`${API}/admin/users/${selectedUser.id}`, updateData);
      toast.success('Benutzer erfolgreich aktualisiert');
      setIsEditOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren des Benutzers');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`);
      toast.success('Benutzer erfolgreich gelöscht');
      setIsDeleteOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Benutzers');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      company: user.company,
      customer_code: user.customer_code || '',
      is_admin: user.is_admin,
      services: user.services || {},
    });
    setIsEditOpen(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setIsDeleteOpen(true);
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getServiceStatus = (services) => {
    const enabled = Object.values(services || {}).filter(s => s === 'enabled').length;
    return enabled;
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.dark }}>Benutzerverwaltung</h1>
            <p className="mt-1" style={{ color: colors.gray }}>
              Verwalten Sie Benutzer und deren Service-Berechtigungen
            </p>
          </div>
          <Button 
            onClick={() => { resetForm(); setIsCreateOpen(true); }} 
            data-testid="create-user-button"
            style={{ background: colors.dark }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Neuer Benutzer
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.gray }} />
          <Input
            placeholder="Suchen nach Name, E-Mail oder Firma..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            style={{ borderColor: colors.border }}
            data-testid="search-users-input"
          />
        </div>

        {/* Users Table */}
        <Card className="bg-white" style={{ borderColor: colors.border }}>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.blue }} />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto mb-4" style={{ color: colors.gray }} />
                <h3 className="text-lg font-medium" style={{ color: colors.dark }}>Keine Benutzer gefunden</h3>
                <p className="mt-2" style={{ color: colors.gray }}>
                  {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff' : 'Erstellen Sie den ersten Benutzer'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>E-Mail</th>
                      <th>Firma</th>
                      <th>Kürzel</th>
                      <th>Rolle</th>
                      <th>Services</th>
                      <th className="text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="font-medium" style={{ color: colors.dark }}>{user.name}</div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2" style={{ color: colors.gray }}>
                            <Mail className="h-4 w-4" />
                            {user.email}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2" style={{ color: colors.gray }}>
                            <Building className="h-4 w-4" />
                            {user.company}
                          </div>
                        </td>
                        <td>
                          <Badge variant="outline" style={{ borderColor: colors.blue, color: colors.blue, fontWeight: 600 }}>
                            {user.customer_code || '---'}
                          </Badge>
                        </td>
                        <td>
                          {user.is_admin ? (
                            <Badge style={{ background: `${colors.blue}20`, color: colors.blue }}>
                              <Shield className="mr-1 h-3 w-3" />
                              Admin
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Benutzer</Badge>
                          )}
                        </td>
                        <td>
                          <Badge variant="outline">
                            {getServiceStatus(user.services)} / {AVAILABLE_SERVICES.length} aktiv
                          </Badge>
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteModal(user)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
              <DialogDescription>
                Füllen Sie die Informationen für den neuen Benutzer aus.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="create-name">Name</Label>
                  <Input
                    id="create-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="create-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-email">E-Mail</Label>
                  <Input
                    id="create-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="create-user-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-password">Passwort</Label>
                  <Input
                    id="create-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="create-user-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-company">Firma</Label>
                  <Input
                    id="create-company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    required
                    data-testid="create-user-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-customer-code">Kundenkürzel (3 Buchstaben)</Label>
                  <Input
                    id="create-customer-code"
                    value={formData.customer_code}
                    onChange={(e) => setFormData({ ...formData, customer_code: e.target.value.toUpperCase().slice(0, 3) })}
                    required
                    maxLength={3}
                    placeholder="z.B. ABC"
                    data-testid="create-user-customer-code"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="create-admin">Administrator</Label>
                  <Switch
                    id="create-admin"
                    checked={formData.is_admin}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                    data-testid="create-user-admin"
                  />
                </div>
                <div className="space-y-3">
                  <Label>Service-Berechtigungen</Label>
                  {AVAILABLE_SERVICES.map((service) => (
                    <div key={service.id} className="flex items-center justify-between">
                      <span className="text-sm">{service.name}</span>
                      <Select
                        value={formData.services[service.id] || 'disabled'}
                        onValueChange={(value) => setFormData({
                          ...formData,
                          services: { ...formData.services, [service.id]: value }
                        })}
                      >
                        <SelectTrigger className="w-32" data-testid={`create-service-${service.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="enabled">Aktiv</SelectItem>
                          <SelectItem value="disabled">Gesperrt</SelectItem>
                          <SelectItem value="hidden">Versteckt</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isSubmitting} data-testid="submit-create-user">
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Erstellen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Benutzer bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie die Informationen des Benutzers.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEdit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="edit-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">E-Mail</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={formData.email}
                    disabled
                    className="opacity-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-password">Neues Passwort (optional)</Label>
                  <Input
                    id="edit-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Leer lassen, um beizubehalten"
                    data-testid="edit-user-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-company">Firma</Label>
                  <Input
                    id="edit-company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    required
                    data-testid="edit-user-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-customer-code">Kundenkürzel (3 Buchstaben)</Label>
                  <Input
                    id="edit-customer-code"
                    value={formData.customer_code}
                    onChange={(e) => setFormData({ ...formData, customer_code: e.target.value.toUpperCase().slice(0, 3) })}
                    required
                    maxLength={3}
                    placeholder="z.B. ABC"
                    data-testid="edit-user-customer-code"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-admin">Administrator</Label>
                  <Switch
                    id="edit-admin"
                    checked={formData.is_admin}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                    data-testid="edit-user-admin"
                  />
                </div>
                <div className="space-y-3">
                  <Label>Service-Berechtigungen</Label>
                  {AVAILABLE_SERVICES.map((service) => (
                    <div key={service.id} className="flex items-center justify-between">
                      <span className="text-sm">{service.name}</span>
                      <Select
                        value={formData.services[service.id] || 'disabled'}
                        onValueChange={(value) => setFormData({
                          ...formData,
                          services: { ...formData.services, [service.id]: value }
                        })}
                      >
                        <SelectTrigger className="w-32" data-testid={`edit-service-${service.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="enabled">Aktiv</SelectItem>
                          <SelectItem value="disabled">Gesperrt</SelectItem>
                          <SelectItem value="hidden">Versteckt</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isSubmitting} data-testid="submit-edit-user">
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie den Benutzer "{selectedUser?.name}" wirklich löschen? 
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isSubmitting}
                data-testid="confirm-delete-user"
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminUsers;
